import React from 'react';
import {
  Card,
  CardMedia,
  Typography,
  Box,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

const BrandCard = ({ 
  brand, 
  onClick, 
  showOverlay = true,
  ...props 
}) => {
  const theme = useTheme();
  const { i18n } = useTranslation();

  const handleCardClick = () => {
    if (onClick) onClick(brand);
  };

  return (
    <Card
      onClick={handleCardClick}
      sx={{
        cursor: 'pointer',
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        overflow: 'hidden',
        height: 140,
        position: 'relative',
        background: theme.palette.background.paper,
        '&:hover': {
          transform: 'translateY(-8px) scale(1.05)',
          boxShadow: `0 12px 24px ${theme.palette.primary.main}25`,
          borderColor: theme.palette.primary.main,
          '& .brand-overlay': {
            opacity: 1,
          },
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: `linear-gradient(135deg, ${theme.palette.primary.main}10 0%, transparent 50%)`,
          opacity: 0,
          transition: 'opacity 0.3s ease',
          zIndex: 1,
        },
        '&:hover::before': {
          opacity: 1,
        },
        ...props.sx
      }}
    >
      {/* Brand Overlay */}
      {showOverlay && (
        <Box 
          className="brand-overlay"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            opacity: 0,
            transition: 'opacity 0.3s ease',
            zIndex: 2,
          }}
        >
          <Typography
            variant="h6"
            fontWeight="bold"
            color={theme.palette.primary.main}
            sx={{ textTransform: 'uppercase' }}
          >
            {brand.title?.[i18n.language] || brand.title?.en || brand.title}
          </Typography>
        </Box>
      )}

      <CardMedia
        component="div"
        sx={{
          height: 140,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: theme.palette.background.paper,
          backgroundImage: `url(${brand.image?.medium_url || brand.image?.url || '/api/placeholder/200/120'})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          padding: 2,
          filter: theme.palette.mode === 'dark' ? 'brightness(0.9)' : 'none',
        }}
      >
        {/* Fallback brand name if no image */}
        {!brand.image?.url && (
          <Typography
            variant="h6"
            fontWeight="bold"
            color="text.secondary"
            textAlign="center"
            sx={{ textTransform: 'uppercase' }}
          >
            {brand.title?.[i18n.language] || brand.title?.en || brand.title}
          </Typography>
        )}
      </CardMedia>
    </Card>
  );
};

export default BrandCard;
